import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { FileText, Plus, Archive as ArchiveIcon, User, LogOut } from 'lucide-react';
import { useUser } from '../lib/userContext';
import { SubtleEngineeringBg } from './SubtleEngineeringBg';
import { ariaLabels } from '../lib/accessibility';
import { MobileNav } from './MobileNav';
const logo = '/assets/logo.webp';

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, signOut } = useUser();
  const isActive = (path: string) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50/50 via-neutral-50 to-orange-50/30 relative">
      <SubtleEngineeringBg />
      
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="absolute top-0 left-0 z-50 bg-orange-500 text-white px-4 py-2 transform -translate-y-full focus:translate-y-0 transition-transform"
      >
        Skip to main content
      </a>
      
      <header className="bg-white border-b-2 border-orange-100 relative z-10 shadow-sm h-[73px] flex items-center">
        <div className="absolute inset-0 opacity-[0.06]">
          <div className="h-full w-full" style={{
            backgroundImage: `
              repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(249, 115, 22, 0.15) 10px, rgba(249, 115, 22, 0.15) 11px)
            `,
          }} />
        </div>
        <div className="px-6 py-4 relative w-full">
          <div className="flex items-center justify-between">
            {/* Left: Logo */}
            <div className="flex-shrink-0">
              <Link to="/dashboard" className="flex items-center gap-2 lg:gap-3">
                <img src={logo} alt="BORI-TECH" className="h-8 w-8 lg:h-12 lg:w-12" />
                <div className="hidden sm:block">
                  <h1 className="text-lg lg:text-xl font-bold text-neutral-900">BORI-TECH</h1>
                  <p className="text-xs lg:text-sm text-neutral-600 hidden md:block">MEP Progress Reports</p>
                </div>
              </Link>
            </div>

            {/* Center: Navigation Links */}
            <nav className="hidden lg:flex items-center justify-center gap-1 absolute left-1/2 -translate-x-1/2" aria-label="Main navigation">
              <Link
                to="/dashboard"
                aria-current={isActive('/dashboard') ? 'page' : undefined}
                aria-label={ariaLabels.navLink('Dashboard', isActive('/dashboard'))}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  isActive('/dashboard')
                    ? 'bg-orange-100 text-orange-900'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                Dashboard
              </Link>

              <Link
                to="/dashboard/create"
                aria-current={isActive('/dashboard/create') ? 'page' : undefined}
                aria-label={ariaLabels.navLink('Create Report', isActive('/dashboard/create'))}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  isActive('/dashboard/create')
                    ? 'bg-orange-100 text-orange-900'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                <Plus className="h-4 w-4" />
                Create Report
              </Link>

              {currentUser.role === 'manager' && (
                <>
                  <Link
                    to="/dashboard/projects"
                    aria-current={isActive('/dashboard/projects') ? 'page' : undefined}
                    aria-label={ariaLabels.navLink('Projects', isActive('/dashboard/projects'))}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                      isActive('/dashboard/projects')
                        ? 'bg-orange-100 text-orange-900'
                        : 'text-neutral-700 hover:bg-neutral-100'
                    }`}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Projects
                  </Link>
                  <Link
                    to="/dashboard/users"
                    aria-current={isActive('/dashboard/users') ? 'page' : undefined}
                    aria-label={ariaLabels.navLink('Users', isActive('/dashboard/users'))}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                      isActive('/dashboard/users')
                        ? 'bg-orange-100 text-orange-900'
                        : 'text-neutral-700 hover:bg-neutral-100'
                    }`}
                  >
                    <User className="h-4 w-4" aria-hidden="true" />
                    Users
                  </Link>
                </>
              )}

              <Link
                to="/dashboard/archive"
                aria-current={isActive('/dashboard/archive') ? 'page' : undefined}
                aria-label={ariaLabels.navLink('Archive', isActive('/dashboard/archive'))}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  isActive('/dashboard/archive')
                    ? 'bg-orange-100 text-orange-900'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                <ArchiveIcon className="h-4 w-4" aria-hidden="true" />
                Archive
              </Link>
            </nav>

            {/* Right: Mobile Nav + User Info + Logout */}
            <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
              {/* Mobile Navigation */}
              <MobileNav />
              <div className="text-right hidden sm:block" role="region" aria-label="User information">
                <p className="text-sm font-medium text-neutral-900">{currentUser.full_name}</p>
                <p className="text-xs text-neutral-600 capitalize">{currentUser.role.replace('_', ' ')}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center" aria-hidden="true">
                <User className="h-5 w-5 text-white" />
              </div>
              <button
                onClick={handleSignOut}
                className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="flex-1 relative z-10" tabIndex={-1}>
        <Outlet />
      </main>

      {/* Build info - helps verify which version is deployed */}
      <div className="fixed bottom-1 right-2 z-50 text-[10px] text-neutral-400 pointer-events-none select-none opacity-60" aria-hidden="true">
        {typeof __COMMIT_HASH__ !== 'undefined' ? `${__COMMIT_HASH__.slice(0, 7)} • ` : ''}
        {typeof __BUILD_TIME__ !== 'undefined' ? new Date(__BUILD_TIME__).toLocaleString() : ''}
      </div>
    </div>
  );
}
